import React, { useState, useEffect, useCallback } from 'react';
import { ImageIcon } from 'lucide-react';
import { Product } from '../../../types/product';
import { ProductImage } from '../../../types/knowledge';
import ProductImagesSection from '../../../components/knowledge/sections/ProductImagesSection';
import knowledgeApiService from '../../../services/api/knowledge/knowledgeApiService';
import { TaskySpinner } from '../../../components/tasky';

interface ProductImagesTabProps {
  currentService: any;
  products?: Product[];
  onProductUpdate?: () => void;
}

/**
 * 見た目調整（DCMモック knowledge-dcm-scoped.html の pi-* 様式に寄せる）。
 * ProductImagesSection は styled-components のため、構造セレクタで上書きする。
 * 機能（アップロード・削除・プライマリ設定・並び替え・URL追加）は一切変更しない。
 * ※ :has() 非対応の古いブラウザでは該当ルールのみ無効化され、従来の見た目にフォールバックする。
 */
const IMAGES_PANEL_STYLES = `
/* カテゴリブロック: 白カード → モックの区切り線様式（.pi-cat） */
.ki-images-body > div > div:has(> div > input[type="file"]) {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0 0 22px;
  margin-bottom: 24px;
  border-bottom: 1px solid #EDEBE5;
}
.ki-images-body > div > div:has(> div > input[type="file"]):last-child {
  padding-bottom: 0;
  margin-bottom: 0;
  border-bottom: none;
}
/* カテゴリヘッダー: 色ドット（.pi-dot） */
.ki-images-body > div > div:has(> div > input[type="file"]) > div:first-child > div:first-child > div:first-child {
  width: 11px;
  height: 11px;
}
/* カテゴリ名（.pi-cat-title） */
.ki-images-body > div > div:has(> div > input[type="file"]) > div:first-child h4 {
  font-size: 15px;
}
/* 件数バッジ（.pi-count） */
.ki-images-body > div > div:has(> div > input[type="file"]) > div:first-child span {
  font-size: 11px;
  padding: 2px 9px;
  border-radius: 999px;
}
/* 追加ボタン / URLから追加（.pi-upload / .pi-urlbtn） */
.ki-images-body > div > div:has(> div > input[type="file"]) > div:first-child button {
  font-size: 12.5px;
  font-weight: 700;
  gap: 6px;
  padding: 8px 15px;
  border-radius: 8px;
}
/* 画像グリッド（.pi-grid） */
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child:has(> div) {
  grid-template-columns: repeat(auto-fill, minmax(228px, 1fr));
  gap: 16px;
}
/* 空状態（.pi-empty） */
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child:not(:has(> div)) {
  padding: 38px;
  background: #FAFAF8;
  border: 1.5px dashed #E8E6E0;
  border-radius: 12px;
}
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child:not(:has(> div)) > p {
  margin: 12px 0 0;
  font-size: 12.5px;
  color: #8A90A4;
}
/* 画像カード（.pi-card） */
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child > div {
  border: 1px solid #E8E6E0;
  border-radius: 12px;
  box-shadow: 0 0 14px rgba(31, 23, 18, 0.04);
}
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child > div:hover {
  border-color: #E8E6E0;
  box-shadow: 0 8px 22px rgba(31, 23, 18, 0.12);
}
/* プライマリバッジ（.pi-primary） */
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child > div img + div {
  font-size: 10px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 6px;
}
/* タイトル・メタ（.pi-title / .pi-sub） */
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child > div > div:has(> h4) {
  padding: 12px 14px 10px;
}
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child > div h4 {
  font-size: 13px;
}
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child > div h4 ~ p:last-child {
  font-size: 10.5px;
  color: #9CA3AF;
}
/* アクション行（.pi-acts / .pi-iconbtn） */
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child > div > div:last-child {
  gap: 6px;
  padding: 9px 14px;
  border-top: 1px solid #F4F3EF;
  background: #FAFAF8;
}
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child > div > div:last-child > button {
  width: 30px;
  height: 30px;
  border: 1px solid #E8E6E0;
  border-radius: 6px;
  color: #6B7280;
}
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child > div > div:last-child > button:hover:not(:disabled) {
  background: #F4F3EF;
  border-color: #E8E6E0;
}
.ki-images-body > div > div:has(> div > input[type="file"]) > div:last-child > div > div:last-child > button:last-child {
  color: #C96162;
}
`;

const ProductImagesTab: React.FC<ProductImagesTabProps> = ({ currentService, products }) => {
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);

  // Find the current product from the service
  const product = products?.find(p => p.id === currentService?.productData?.id);

  const fetchProductImages = useCallback(async () => {
    if (!product) return;
    setLoadingImages(true);
    try {
      const images = await knowledgeApiService.getProductImages(product.id);
      setProductImages(images);
    } catch (error) {
      console.error('Failed to fetch product images:', error);
    } finally {
      setLoadingImages(false);
    }
  }, [product]);

  useEffect(() => {
    if (product) {
      fetchProductImages();
    }
  }, [product, fetchProductImages]);

  const handleImagesChange = useCallback((images: ProductImage[]) => {
    setProductImages(images);
  }, []);

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <ImageIcon className="w-12 h-12 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">商品を選択してください</p>
      </div>
    );
  }

  if (loadingImages) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <TaskySpinner size="lg" className="mb-4" />
        <p className="text-sm text-gray-500">商品画像を読み込み中...</p>
      </div>
    );
  }

  return (
    <div>
      <style>{IMAGES_PANEL_STYLES}</style>

      {/* DCMモック準拠のパネル見出し */}
      <div className="mt-2 flex items-end justify-between">
        <h2 className="flex items-center gap-2 text-lg font-bold text-dark">
          <span className="material-icons-round text-[21px] text-primary">image</span>
          商品画像
          <span className="help-tip">
            <span className="material-icons-round">help_outline</span>
            <span className="tip-body">
              <b>バナー生成に使う画像。</b><b>プライマリ</b>に設定した画像は、
              バナー生成で優先的に使われます。
            </span>
          </span>
        </h2>
      </div>

      <div className="ki-images-body mt-3">
        <ProductImagesSection
          productId={product.id}
          images={productImages}
          onImagesChange={handleImagesChange}
        />
      </div>
    </div>
  );
};

export default ProductImagesTab;
